import { Network } from '@capacitor/network';

/**
 * Synchronously-readable device connectivity flag.
 *
 * Kept up to date by the Capacitor Network plugin (authoritative on native,
 * also works on web) and the browser `online`/`offline` events as a fallback.
 *
 * The point of a synchronous getter is so the API layer (axios interceptor)
 * and Sentry's `beforeSend` can tell two very different failures apart without
 * awaiting anything:
 *   - "the device has no internet"  → expected, never report (pure noise)
 *   - "the device is online but we can't reach our server" → a real
 *     server-connectivity problem worth tracking.
 */
let online: boolean =
    typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
        ? navigator.onLine
        : true;

export function isDeviceOnline(): boolean {
    return online;
}

// Browser fallback — fires reliably in web/PWA contexts even if the Capacitor
// plugin is unavailable.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('online', () => { online = true; });
    window.addEventListener('offline', () => { online = false; });
}

// Capacitor Network plugin — authoritative on native. Best-effort: if the
// plugin isn't available we silently fall back to the navigator/window value.
void (async () => {
    try {
        const status = await Network.getStatus();
        online = status.connected;
        await Network.addListener('networkStatusChange', (status) => {
            online = status.connected;
        });
    } catch {
        /* plugin unavailable — rely on navigator/window events */
    }
})();
